import React, { useState, useEffect } from "react";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import { Dialog } from "component/shared/dialog";
import { DialogTitle } from "component/shared/dialog/title";
import { Select } from "component/shared/select";
import { useSelector } from "react-redux";
import { useAppThunkDispatch } from "redux/store";
import { updateCampaign } from "redux/slice/campaigns";
import { getSortedCampaignDisplayCategories } from "redux/selector";
import { Campaign } from "model/campaign";
import {
	categorySelectOptions,
	categoryValueToFk,
	categoryFkToValue,
	findDefaultCategory,
	UNCATEGORIZED_ID,
	UNCATEGORIZED_NAME,
} from "shared/campaign-grouping";
import { styles } from "./style";

interface OwnProps {
	// The campaign being moved. `undefined` keeps the dialog closed.
	campaign?: Campaign;
	// Display name of the campaign's current category (source), for the body copy.
	sourceName?: string;
	onClose: () => void;
}

interface Props extends OwnProps, WithStyles<typeof styles> {}

/**
 * MAX-3878 — Move to Category modal (figma 1013:14804). Copy verbatim: title "Move to Category",
 * body "Move {name} from {source} to:", dropdown "Category" (default General), buttons
 * "Cancel" / "Move Campaign". Move reuses updateCampaign with the new campaignDisplayCategoryId
 * (null ⇒ Uncategorized); the AR6 optimistic merge makes the grouped view update immediately.
 */
const Component: React.FC<Props> = ({ campaign, sourceName, onClose, classes = {} }) => {
	const categories = useSelector(getSortedCampaignDisplayCategories);
	const dispatch = useAppThunkDispatch();
	const open = !!campaign;

	const options = categorySelectOptions(categories, { includeUncategorized: true });
	const defaultDestinationId = findDefaultCategory(categories)?.id ?? UNCATEGORIZED_ID;

	const [destination, setDestination] = useState<number>(defaultDestinationId);

	// Preselect the campaign's current category when the dialog opens (fall back to General).
	useEffect(() => {
		if (campaign) {
			setDestination(
				campaign.campaignDisplayCategoryId != null
					? categoryFkToValue(campaign.campaignDisplayCategoryId)
					: defaultDestinationId,
			);
		}
	}, [campaign]);

	const handleMove = () => {
		if (!campaign) {
			return;
		}
		dispatch(updateCampaign({
			campaign: { ...campaign, campaignDisplayCategoryId: categoryValueToFk(destination) },
		}));
		onClose();
	};

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
			<DialogTitle title="Move to Category" onClose={onClose} />
			<Mui.DialogContent>
				<Mui.Typography className={classes.body}>
					Move <strong>{campaign?.name}</strong> from <strong>{sourceName || UNCATEGORIZED_NAME}</strong> to:
				</Mui.Typography>
				<Select
					variant="outlined"
					margin="dense"
					label="Category"
					required
					fullWidth
					value={destination}
					options={options}
					valueExtractor={(c) => c.id}
					labelExtractor={(c) => c.name}
					optionRenderer={(c) => <Mui.ListItemText primary={c.name} />}
					onChange={(value) => setDestination(value)}
				/>
			</Mui.DialogContent>
			<Mui.DialogActions className={classes.actions}>
				<Mui.Button variant="outlined" color="primary" onClick={onClose}>
					Cancel
				</Mui.Button>
				<Mui.Button variant="contained" color="primary" onClick={handleMove}>
					Move Campaign
				</Mui.Button>
			</Mui.DialogActions>
		</Dialog>
	);
};

export const MoveCampaignDialog = withStyles(Component, styles);
